import { motion } from "framer-motion";
import { 
  Target, 
  Megaphone, 
  Handshake, 
  UserCheck, 
  BarChart3, 
  ArrowRight, 
  Check,
  TrendingUp,
  Zap,
  Users,
  Database
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

// Mock UI Components for each system card
const GTMFlowVisual = () => {
  const { t } = useLanguage();
  return (
    <div className="mt-6 p-4 bg-background/50 rounded-xl border border-border/30">
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 text-center p-3 bg-primary/10 rounded-lg border border-primary/20">
          <Users className="w-5 h-5 text-primary mx-auto mb-1" />
          <span className="text-xs text-muted-foreground">{t("revenueSystem.gtm.market")}</span>
        </div>
        <ArrowRight className="w-4 h-4 text-primary animate-pulse-glow" />
        <div className="flex-1 text-center p-3 bg-secondary/10 rounded-lg border border-secondary/20">
          <Target className="w-5 h-5 text-secondary mx-auto mb-1" />
          <span className="text-xs text-muted-foreground">{t("revenueSystem.gtm.icp")}</span>
        </div>
        <ArrowRight className="w-4 h-4 text-primary animate-pulse-glow" />
        <div className="flex-1 text-center p-3 bg-accent/10 rounded-lg border border-accent/20">
          <Check className="w-5 h-5 text-accent mx-auto mb-1" />
          <span className="text-xs text-muted-foreground">{t("revenueSystem.gtm.fit")}</span>
        </div>
      </div>
    </div>
  );
};

const LeadGenFlowVisual = () => {
  const { t } = useLanguage();
  return (
    <div className="mt-6 p-4 bg-background/50 rounded-xl border border-border/30">
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="p-3 bg-insight/10 rounded-lg border border-insight/20 text-center">
          <Zap className="w-5 h-5 text-insight mx-auto mb-1" />
          <span className="text-xs text-muted-foreground">{t("revenueSystem.leadgen.signals")}</span>
        </div>
        <div className="p-3 bg-secondary/10 rounded-lg border border-secondary/20 text-center">
          <Megaphone className="w-5 h-5 text-secondary mx-auto mb-1" />
          <span className="text-xs text-muted-foreground">{t("revenueSystem.leadgen.channels")}</span>
        </div>
      </div>
      <div className="flex justify-center">
        <ArrowRight className="w-4 h-4 text-primary rotate-90 animate-pulse-glow" />
      </div>
      <div className="mt-2 p-3 bg-primary/10 rounded-lg border border-primary/20 text-center">
        <Database className="w-5 h-5 text-primary mx-auto mb-1" />
        <span className="text-xs text-muted-foreground">{t("revenueSystem.leadgen.pipeline")}</span>
      </div>
    </div>
  );
};

const SalesEnablementVisual = () => {
  const { t } = useLanguage();
  return (
    <div className="mt-6 p-4 bg-background/50 rounded-xl border border-border/30">
      <div className="p-3 bg-card rounded-lg border border-border/50 mb-3">
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs text-muted-foreground">{t("revenueSystem.sales.dealCard")}</span>
          <span className="text-xs font-semibold text-accent">$25,000</span>
        </div>
        <div className="text-sm font-medium text-foreground">Enterprise Client</div>
      </div>
      <div className="flex items-center gap-1.5">
        {[1, 2, 3].map((i) => (
          <div key={i} className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
            <Check className="w-3 h-3 text-primary-foreground" />
          </div>
        ))}
        {[4, 5].map((i) => (
          <div key={i} className="w-6 h-6 rounded-full bg-muted border border-border" />
        ))}
        <span className="text-xs text-muted-foreground ml-2">{t("revenueSystem.sales.stage")}</span>
      </div>
    </div>
  );
};

const ActivationVisual = () => {
  const { t } = useLanguage();
  return (
    <div className="mt-6 p-4 bg-background/50 rounded-xl border border-border/30">
      <div className="mb-4">
        <div className="flex justify-between text-xs mb-2">
          <span className="text-muted-foreground">{t("revenueSystem.activation.onboarding")}</span>
          <span className="text-accent font-semibold">75%</span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div className="h-full w-3/4 bg-gradient-to-r from-primary to-accent rounded-full" />
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm">
          <div className="w-5 h-5 rounded bg-accent/20 flex items-center justify-center">
            <Check className="w-3 h-3 text-accent" />
          </div>
          <span className="text-foreground">{t("revenueSystem.activation.activated")}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <div className="w-5 h-5 rounded bg-accent/20 flex items-center justify-center">
            <Check className="w-3 h-3 text-accent" />
          </div>
          <span className="text-foreground">{t("revenueSystem.activation.adopted")}</span>
        </div>
      </div>
    </div>
  );
};

const RevenueOpsVisual = () => {
  const { t } = useLanguage();
  return (
    <div className="mt-6 p-4 bg-background/50 rounded-xl border border-border/30">
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <span className="text-xs text-muted-foreground">{t("revenueSystem.revops.revenue")}</span>
          <div className="text-lg font-bold text-foreground">$1.2M</div>
          <div className="flex items-center gap-1 text-xs text-accent">
            <TrendingUp className="w-3 h-3" />
            <span>+12%</span>
          </div>
        </div>
        <div>
          <span className="text-xs text-muted-foreground">{t("revenueSystem.revops.pipeline")}</span>
          <div className="text-lg font-bold text-foreground">$3.5M</div>
          <div className="flex items-center gap-1 text-xs text-accent">
            <TrendingUp className="w-3 h-3" />
            <span>+8%</span>
          </div>
        </div>
      </div>
      <div className="flex items-end gap-1 h-12">
        {[40, 55, 45, 70, 60, 85, 75].map((height, i) => (
          <div
            key={i}
            className="flex-1 bg-gradient-to-t from-primary/60 to-primary rounded-t"
            style={{ height: `${height}%` }}
          />
        ))}
      </div>
    </div>
  );
};

export function RevenueGrowthSystemSection() {
  const { t } = useLanguage();

  const systemCards = [
    {
      icon: Target,
      title: t("revenueSystem.gtm.title"),
      description: t("revenueSystem.gtm.description"),
      Visual: GTMFlowVisual,
    },
    {
      icon: Megaphone,
      title: t("revenueSystem.leadgen.title"),
      description: t("revenueSystem.leadgen.description"),
      Visual: LeadGenFlowVisual,
    },
    {
      icon: Handshake,
      title: t("revenueSystem.sales.title"),
      description: t("revenueSystem.sales.description"),
      Visual: SalesEnablementVisual,
    },
    {
      icon: UserCheck,
      title: t("revenueSystem.activation.title"),
      description: t("revenueSystem.activation.description"),
      Visual: ActivationVisual,
    },
  ];

  const revenueOpsCard = {
    icon: BarChart3,
    title: t("revenueSystem.revops.title"),
    description: t("revenueSystem.revops.description"),
    Visual: RevenueOpsVisual,
  };

  return (
    <section id="revenue-growth-system" className="py-20 lg:py-32 bg-card/30 relative overflow-hidden">
      {/* Subtle background glow */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,hsl(var(--primary)/0.08)_0%,transparent_70%)]" />
      
      <div className="section-container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t("revenueSystem.title")}{" "}
            <span className="text-primary">{t("revenueSystem.titleHighlight")}</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t("revenueSystem.subtitle")}
          </p>
        </motion.div>

        {/* First 4 cards in 2x2 grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {systemCards.map((card, index) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="card-surface p-6 lg:p-8 hover-lift hover-glow border border-border/50 hover:border-primary/30 transition-all duration-300 group"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors glow-effect">
                  <card.icon className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-xl font-bold text-foreground mb-2">
                    {card.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {card.description}
                  </p>
                </div>
              </div>
              <card.Visual />
            </motion.div>
          ))}
        </div>

        {/* 5th card centered */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="max-w-2xl mx-auto"
        >
          <div className="card-surface p-6 lg:p-8 hover-lift hover-glow border border-border/50 hover:border-primary/30 transition-all duration-300 group">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors glow-effect">
                <revenueOpsCard.icon className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-xl font-bold text-foreground mb-2">
                  {revenueOpsCard.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {revenueOpsCard.description}
                </p>
              </div>
            </div>
            <revenueOpsCard.Visual />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
